import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Checkbox, CloseButton, Container, Flex,
  Heading, HStack, IconButton, Image, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, ModalFooter, List, ListIcon, ListItem, Select, Spinner, Stack,
  Text, Textarea, useDisclosure, useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PictoolsMark } from '../components/PictoolsMark';
import { useDispatch, useSelector } from 'react-redux';
import { ID3Writer } from 'browser-id3-writer';
import { catalogCoverImage } from '../catalog/assets';
import { useAuth } from '../auth/AuthProvider';
import { useCreateJobMutation, useDraftLyricsMutation, useGetJobQuery, PresetDoc } from '../redux/services/mixgenie';
import { PresetPicker } from '../generator/PresetPicker';
import { PresetChips } from '../generator/PresetChips';
import { GenerationOverlay } from '../generator/GenerationOverlay';
import { RootState } from '../redux/store';
import { setReferenceId } from '../redux/features/localAudioSlice';
import { setQueue } from '../redux/features/playerSlice';

/**
 * Reference audio ships to PromptLab which forwards to Mureka via multipart
 * → an S3 upload PromptLab signs server-side. Two things have to be tight
 * or the S3 signature calculation breaks and we get "SignatureDoesNotMatch":
 *
 *   1. Filename MUST be ASCII-only, no unicode / no ellipsis / no `<>:"|?*`.
 *      Web Share Target hands us the OS-picked filename which often carries
 *      unicode (…, curly quotes, non-latin chars from streaming apps) that
 *      trips PromptLab's Node form-data canonicalization vs. AWS SigV4.
 *
 *   2. Size cap ~ 8 MB. Mureka only uses the first ~60 s as a musical
 *      reference anyway; shipping a whole album track wastes upload time
 *      and has repeatedly correlated with 5xx from PromptLab's S3 stage.
 *      MP3 is stream-tolerant to truncation (frames are self-contained),
 *      so byte-slicing the head of the file gives a valid, short mp3.
 */
const REFERENCE_MAX_BYTES = 8 * 1024 * 1024;

function sanitizeReferenceFileName(name: string, mime: string): string {
  const ext = mime === 'audio/mpeg' ? '.mp3'
    : mime === 'audio/mp4' ? '.m4a'
    : mime === 'audio/wav' ? '.wav'
    : mime === 'audio/ogg' ? '.ogg'
    : /\.\w{2,4}$/.test(name) ? name.slice(name.lastIndexOf('.')) : '.mp3';
  const stem = (name || 'reference')
    .replace(/\.[^.]+$/, '')                // drop existing extension
    .normalize('NFKD')                       // decompose accented chars
    .replace(/[̀-ͯ]/g, '')          // strip combining marks after NFKD
    .replace(/[^\x20-\x7E]+/g, '')           // drop any remaining non-ASCII
    .replace(/[<>:"|?*\\/]+/g, ' ')          // drop path/filesystem specials
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60) || 'reference';
  return `${stem}${ext}`;
}

// ISO-code → full English name that the LLM prompt templates expect.
// Kept as a plain map (small, closed set) to avoid a runtime dep on
// Intl.DisplayNames for such a short list.
const LANG_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese',
};

async function fileToReferenceAudio(file: File): Promise<{ fileName: string; mimeType: string; base64: string; size: number }> {
  let buf = await file.arrayBuffer();
  // Head-clip long files. MP3 tolerates byte-truncation; other codecs will
  // usually decode up to the last complete frame in the truncated buffer.
  if (buf.byteLength > REFERENCE_MAX_BYTES) {
    buf = buf.slice(0, REFERENCE_MAX_BYTES);
  }
  const bytes = new Uint8Array(buf);
  // btoa can't handle raw binary strings above one octet — batch through
  // Uint8Array then convert to a binary string chunk by chunk to keep the
  // stack small for larger files.
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  const mimeType = file.type || 'audio/mpeg';
  return {
    fileName: sanitizeReferenceFileName(file.name, mimeType),
    mimeType,
    base64: btoa(binary),
    size: bytes.length,
  };
}

/**
 * Download an audio URL as an mp3 with ID3 tags rewritten to `title` + MixGenie.
 * Falls back to direct navigation if fetch/ID3 rewrite fails (CORS, etc).
 */
async function downloadSongWithTags(url: string, title: string): Promise<void> {
  const filename = (title || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80).trim() || 'song';
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch ${res.status}`);
    const buf = await res.arrayBuffer();
    const writer = new ID3Writer(buf);
    writer.setFrame('TIT2', title).setFrame('TPE1', ['MixGenie']).setFrame('TALB', 'MixGenie');
    writer.addTag();
    const blob = writer.getBlob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${filename}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.mp3`;
    a.click();
  }
}

/**
 * Generator page — deliberately minimal:
 *   1. optional prompt
 *   2. optional lyrics + "instrumental" checkbox (mutually exclusive)
 *   3. "Selection" strip (reference song chip + prompt preview) — the
 *      pictools-parallel to the effect/prompt filter zone
 *   4. Generate button
 *
 * If the visitor arrived from a song page (via `generatorReferencePath`),
 * the reference chip is pre-filled from URL params. The chip is removable —
 * removing it makes this a from-scratch generation.
 */
export default function GeneratorPage() {
  const { t, i18n } = useTranslation();
  // Language of the lyrics — separate from the app UI language so a user
  // browsing the Spanish UI can still generate an English song. Default to
  // the app's current locale (i18n.language, ISO code); the picker below
  // lets the user override before drafting or generating.
  const initialLyricLang = (i18n.language || 'es').slice(0, 2).toLowerCase();
  const [lyricsLanguage, setLyricsLanguage] = useState<string>(
    ['en', 'es', 'pt'].includes(initialLyricLang) ? initialLyricLang : 'es',
  );
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const paywall = useDisclosure();

  // --- Reference (from ?referenceTitle=&referenceArtist=…) --------------
  const initialRef = useMemo(() => {
    const title = params.get('referenceTitle') || '';
    const artist = params.get('referenceArtist') || '';
    if (!title && !artist) return null;
    return {
      title,
      artist,
      slug: params.get('referenceSlug') || '',
      artistSlug: params.get('referenceArtistSlug') || '',
      cover: params.get('referenceCover') || '',
      styleNotes: params.get('styleNotes') || '',
    };
  }, [params]);

  const [reference, setReference] = useState(initialRef);
  const dispatch = useDispatch();
  // Local reference file picked from the device library (Redux slice; File
  // object held in memory). When set, it wins over the catalog reference —
  // we ship the user's own audio to Mureka instead of the iTunes preview.
  const localReference = useSelector((s: RootState) => {
    const id = s.localAudio.referenceId;
    if (!id) return null;
    return s.localAudio.tracks.find((t) => t.id === id) || null;
  });
  const clearLocalReference = () => dispatch(setReferenceId(null));
  const [prompt, setPrompt] = useState<string>(params.get('vibe') || params.get('prompt') || '');
  const [lyrics, setLyrics] = useState<string>('');
  const [instrumental, setInstrumental] = useState<boolean>(false);
  // Opt-in cover art with the song (+1 credit → 2 total). Never on by default.
  const [withCover, setWithCover] = useState<boolean>(false);
  // Applied presets — one array per target. `promptFragment`s from `soundPresets`
  // are joined to the style prompt sent to Mureka; `lyricsPresets` become
  // a thematic brief prepended to the lyrics prompt (or to the Draft-lyrics
  // request when the user hits ✨ Draft lyrics).
  const [soundPresets, setSoundPresets] = useState<PresetDoc[]>([]);
  const [lyricsPresets, setLyricsPresets] = useState<PresetDoc[]>([]);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createJob] = useCreateJobMutation();
  const [draftLyrics, { isLoading: isDraftingLyrics }] = useDraftLyricsMutation();
  const { data: jobData } = useGetJobQuery(currentJobId!, {
    skip: !currentJobId,
    pollingInterval: currentJobId ? 3000 : 0,
  });

  const currentJob = jobData?.data;
  const isJobComplete = currentJob?.status === 'completed' || currentJob?.status === 'failed';
  if (isGenerating && isJobComplete) setIsGenerating(false);

  // When the job finishes, autoplay the result through the global player so
  // the Done card doesn't need its own <audio>. `autoloadedForJobId` guards
  // against re-loading the queue on every poll after the initial completion.
  const [autoloadedForJobId, setAutoloadedForJobId] = useState<string | null>(null);
  useEffect(() => {
    if (
      currentJob?.status === 'completed'
      && currentJobId
      && currentJobId !== autoloadedForJobId
    ) {
      const audioUrl = currentJob.outputAudioUrl || currentJob.outputUrl;
      if (audioUrl) {
        dispatch(setQueue({
          tracks: [{
            id: String(currentJob._id || currentJobId),
            title: currentJob.title || t('generator.untitled', 'MixGenie song'),
            artist: 'MixGenie',
            audioUrl,
          }],
          startIndex: 0,
        }));
        setAutoloadedForJobId(currentJobId);
      }
    }
  }, [currentJob, currentJobId, autoloadedForJobId, dispatch, t]);

  const removeReference = () => {
    setReference(null);
    // Strip reference params so a page refresh doesn't restore it.
    ['referenceMode', 'referenceTitle', 'referenceArtist', 'referenceSlug', 'referenceArtistSlug', 'referenceCover', 'vibe', 'styleNotes']
      .forEach((k) => params.delete(k));
    setParams(params, { replace: true });
  };

  // Base signal must be present (prompt OR reference OR local reference OR
  // a sound preset the user tapped). Additionally, vocal generation needs
  // SOMETHING to sing — user lyrics, a lyric-theme preset, or instrumental.
  // Without one of those Mureka fabricates lyrics from the style text
  // (the "sings the reference-song title" bug).
  const hasBaseSignal = prompt.trim().length > 0 || !!reference || !!localReference || soundPresets.length > 0;
  const hasLyricsIntent = instrumental || lyrics.trim().length > 0 || lyricsPresets.length > 0;
  const canGenerate = hasBaseSignal && hasLyricsIntent && !isGenerating;

  /**
   * Fold applied preset fragments into the user's prompts. Prompt fragments
   * are appended (comma-separated) so the user's own words lead. Lyric-theme
   * fragments become a bracketed brief ahead of the user's lyrics so Mureka
   * sees the intent even when the user hasn't drafted anything yet.
   */
  const composeStyle = (): string => {
    const base = prompt.trim();
    const fragments = soundPresets.map((p) => p.promptFragment).filter(Boolean);
    return [base, ...fragments].filter(Boolean).join('. ');
  };
  /**
   * The lyrics we hand to Mureka are the user's textarea contents verbatim
   * — Mureka SINGS this text, so it must not contain meta-instructions like
   * "[Theme: birthday ...]" or the model will try to sing those words.
   * Themes drive the auto-draft (which produces real lyrics); after that
   * the textarea is the single source of truth.
   */
  const composeLyrics = (): string | undefined => {
    if (instrumental) return undefined;
    return lyrics.trim() || undefined;
  };

  /**
   * Ask PromptLab to draft lyrics. `themePresets` (when passed) win as the
   * primary brief so that picking "Birthday" from the lyric-theme picker
   * generates a birthday song without needing a prompt or reference set —
   * that's the whole point of the presets. When no theme is passed we fall
   * back to the current field state (prompt / reference).
   */
  const handleDraftLyrics = async (themePresets?: PresetDoc[]) => {
    const themes = themePresets ?? lyricsPresets;
    const themeBrief = themes.map((p) => p.promptFragment).filter(Boolean).join(' · ');
    try {
      const res: any = await draftLyrics({
        // `brief` takes precedence on the BE — hand it the theme text so
        // even a bare "Birthday" selection produces lyrics.
        brief: themeBrief || undefined,
        prompt: prompt.trim() || undefined,
        referenceTitle: reference?.title || undefined,
        referenceArtist: reference?.artist || undefined,
        // Language chosen in the picker next to the Draft button. Backend
        // takes the English name of the language (Spanish/English/Portuguese)
        // and passes it to the lyrics-generation prompt.
        language: LANG_NAMES[lyricsLanguage] || 'Spanish',
      }).unwrap();
      const drafted = res?.data?.lyrics || res?.lyrics;
      if (drafted) {
        setLyrics(drafted);
        setInstrumental(false);
      } else {
        toast({ title: t('generator.lyricsDraftEmpty', 'Could not draft lyrics — try adding a prompt or a theme.'), status: 'warning' });
      }
    } catch (err: any) {
      toast({ title: err?.data?.message || t('generator.lyricsDraftFailed', 'Draft failed'), status: 'error' });
    }
  };

  /**
   * Handler for the lyric-theme picker's Apply. Sets the applied presets
   * AND auto-drafts fresh lyrics from those themes — user asked for
   * "select a lyrics theme → lyrics generated with just that". We only
   * auto-draft when new themes were actually added (removing a theme
   * should leave the current lyrics alone).
   */
  const applyLyricsPresets = (next: PresetDoc[]) => {
    const prevIds = new Set(lyricsPresets.map((p) => String(p._id)));
    const added = next.filter((p) => !prevIds.has(String(p._id)));
    setLyricsPresets(next);
    if (added.length > 0) {
      // Draft against the FULL new selection (not just the added subset)
      // so multi-theme composition ("Birthday" + "For my love") works.
      handleDraftLyrics(next);
    }
  };

  // Timestamp of the last submit. Guards against Mureka 429s when the user
  // rage-taps "Generate another" — Mureka rate-limits per-account and each
  // 429 still burns a network round-trip. 3s is enough to defeat double-tap
  // while feeling instantaneous under normal use.
  const lastSubmitRef = useRef<number>(0);
  const handleGenerate = async () => {
    if (!canGenerate) {
      setError(t('generator.needPromptOrReference', 'Add a prompt or a reference song to generate.'));
      return;
    }
    const now = Date.now();
    if (now - lastSubmitRef.current < 3000) {
      setError(t('generator.tooFast', 'Whoa — hold on a couple of seconds before generating again.'));
      return;
    }
    lastSubmitRef.current = now;
    setError(null);
    setIsGenerating(true);
    // Reset previous job state so the status card doesn't show "Done" from
    // the last run while the new one submits, and so the auto-play effect
    // fires again once the new job completes (autoloadedForJobId guards
    // against re-loading the same track, so it needs to be cleared here).
    setCurrentJobId(null);
    setAutoloadedForJobId(null);

    // Compose the final style/lyrics text (user text + applied presets),
    // then fall back to a reference-derived description if the whole style
    // string would still be empty.
    const composedStyle = composeStyle();
    const composedLyrics = composeLyrics();
    const vibe = composedStyle
      || (reference ? `Create an original song inspired by ${reference.title} by ${reference.artist}.` : '');

    try {
      // Local reference wins — user-picked file gets uploaded verbatim as
      // referenceAudio; backend then skips the iTunes lookup because the
      // referenceAudio field is already populated.
      const referenceAudio = localReference ? await fileToReferenceAudio(localReference.file) : undefined;

      const jobPayload: any = {
        email: user?.email,
        userId: user?.id || user?._id,
        vibe,
        prompt: composedStyle || undefined,
        // The user's OWN typed text only — no preset fragments folded in.
        // Backend uses this for the auto-title so tapping "Hopeful" doesn't
        // name every song "makes the listener feel hopeful...".
        userPromptText: prompt.trim() || undefined,
        instrumental,
        generateCover: withCover,
        lyrics: composedLyrics,
        // Language of the vocals. Backend threads this into the style
        // synthesizer so Mureka is told to sing in the right language
        // (matters most when lyrics are drafted at generation time or
        // when they're multilingual/ambiguous).
        lyricsLanguage: !instrumental ? (LANG_NAMES[lyricsLanguage] || undefined) : undefined,
        referenceMode: (localReference || reference) ? (localReference ? 'audio-snippet' : 'title-artist') : undefined,
        referenceTitle: reference?.title || undefined,
        referenceArtist: reference?.artist || undefined,
        referenceSlug: reference?.slug || undefined,
        referenceArtistSlug: reference?.artistSlug || undefined,
        styleNotes: reference?.styleNotes || undefined,
        referenceAudio,
      };
      const result = await createJob(jobPayload).unwrap();
      if (result?.code === 'INSUFFICIENT_CREDITS') {
        paywall.onOpen();
        setIsGenerating(false);
        return;
      }
      setCurrentJobId(result?.data?.id || result?.data?._id);
    } catch (err: any) {
      if (err?.data?.code === 'INSUFFICIENT_CREDITS' || err?.status === 402) {
        paywall.onOpen();
      } else {
        setError(err?.data?.message || t('generator.failed', 'Generation failed. Please try again.'));
      }
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Container maxW="640px" py={{ base: 2, md: 4 }} px={0}>
        <Stack spacing={2} mb={7} align="flex-start">
          <Heading
            as="h1"
            fontSize={{ base: '30px', md: '36px' }}
            fontWeight={800}
            lineHeight="1.1"
            letterSpacing="-0.015em"
            color="whiteAlpha.900"
            style={{ textWrap: 'balance' as any }}
          >
            {t('generator.title', 'Create your song')}
          </Heading>
          <Text color="whiteAlpha.700" fontSize={{ base: 'sm', md: 'md' }} style={{ textWrap: 'balance' as any }}>
            {t('generator.subtitle', 'Optional prompt, optional lyrics. Base it on a reference song or start from scratch.')}
          </Text>
        </Stack>

        {/* --- Optional prompt + sound presets --- */}
        <FieldBlock
          label={t('generator.promptLabel', 'Prompt')}
          hint={t('generator.promptOptional', 'Optional. Describe the vibe, style, era, instruments...')}
          right={
            <PresetPicker
              target="sound"
              selectedIds={soundPresets.map((p) => String(p._id))}
              onApply={setSoundPresets}
              triggerLabel={t('generator.addSoundPreset', 'Add style / instrument')}
            />
          }
        >
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('generator.promptPlaceholder', 'e.g. Dark synth-wave with driving bass, cinematic feel, 2020s production')}
            bg="rgba(10, 6, 22, 0.55)"
            color="whiteAlpha.900"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="12px"
            _hover={{ borderColor: 'whiteAlpha.300' }}
            _focus={{ borderColor: 'pink.300', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)', bg: 'rgba(10, 6, 22, 0.75)' }}
            _placeholder={{ color: 'whiteAlpha.400' }}
            rows={3}
            resize="vertical"
          />
          {soundPresets.length > 0 && (
            <Box mt={3}>
              <PresetChips
                presets={soundPresets}
                onRemove={(id) => setSoundPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            </Box>
          )}
        </FieldBlock>

        {/* --- Lyrics + instrumental toggle + draft-lyrics button --- */}
        <FieldBlock
          label={t('generator.lyricsLabel', 'Lyrics')}
          hint={t('generator.lyricsRequiredUnlessInstr', "Write your lyrics, click Draft to have MixGenie propose some, or tick Instrumental.")}
          right={
            <HStack spacing={3} wrap="wrap">
              {!instrumental && (
                <PresetPicker
                  target="lyrics"
                  selectedIds={lyricsPresets.map((p) => String(p._id))}
                  onApply={applyLyricsPresets}
                  triggerLabel={t('generator.addLyricTheme', 'Add lyric theme')}
                />
              )}
              {!instrumental && (
                <Select
                  size="xs"
                  value={lyricsLanguage}
                  onChange={(e) => setLyricsLanguage(e.target.value)}
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.300"
                  color="whiteAlpha.900"
                  width="auto"
                  aria-label={t('generator.lyricsLanguageLabel', 'Language')}
                >
                  <option value="es" style={{ color: '#000' }}>🇪🇸 Español</option>
                  <option value="en" style={{ color: '#000' }}>🇬🇧 English</option>
                  <option value="pt" style={{ color: '#000' }}>🇵🇹 Português</option>
                </Select>
              )}
              {!instrumental && (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="pink"
                  onClick={() => handleDraftLyrics()}
                  isLoading={isDraftingLyrics}
                  // Enable whenever there's ANY brief we can hand to the LLM
                  // — including a lyric theme on its own.
                  isDisabled={!hasBaseSignal && lyricsPresets.length === 0}
                  data-cta="generator:draft-lyrics"
                >
                  ✨ {t('generator.draftLyrics', 'Draft lyrics')}
                </Button>
              )}
              <Checkbox
                isChecked={instrumental}
                onChange={(e) => {
                  setInstrumental(e.target.checked);
                  if (e.target.checked) setLyricsPresets([]);
                }}
                colorScheme="pink"
                size="sm"
              >
                <Text color="whiteAlpha.800" fontSize="sm">{t('generator.instrumental', 'Instrumental only')}</Text>
              </Checkbox>
            </HStack>
          }
        >
          <Textarea
            value={lyrics}
            onChange={(e) => setLyrics(e.target.value)}
            placeholder={
              instrumental
                ? t('generator.instrumentalOn', 'Instrumental selected — lyrics disabled.')
                : t('generator.lyricsPlaceholder', 'Write or paste your lyrics here. Click ✨ Draft lyrics to have MixGenie propose some.')
            }
            bg="rgba(10, 6, 22, 0.55)"
            color="whiteAlpha.900"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="12px"
            _hover={{ borderColor: 'whiteAlpha.300' }}
            _focus={{ borderColor: 'pink.300', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)', bg: 'rgba(10, 6, 22, 0.75)' }}
            _placeholder={{ color: 'whiteAlpha.400' }}
            rows={8}
            resize="vertical"
            isDisabled={instrumental}
            opacity={instrumental ? 0.5 : 1}
          />
          {!instrumental && lyricsPresets.length > 0 && (
            <Box mt={3}>
              <PresetChips
                presets={lyricsPresets}
                onRemove={(id) => setLyricsPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            </Box>
          )}
        </FieldBlock>

        {/* --- Selection zone (pictools-parallel filter chip strip) --- */}
        <Box mb={5}>
          <Text
            fontSize="11px"
            fontWeight={700}
            color="whiteAlpha.700"
            mb={2}
            textTransform="uppercase"
            letterSpacing="0.1em"
            px={1}
          >
            {t('generator.selection', 'Selection')}
          </Text>
          <Flex
            wrap="wrap"
            gap={2}
            align="center"
            p={3}
            bg="rgba(20, 14, 36, 0.45)"
            backdropFilter="blur(10px)"
            borderRadius="14px"
            border="1px solid"
            borderColor="whiteAlpha.150"
          >
            {localReference && (
              <HStack spacing={2} pl={3} pr={1} py={1} bg="accent.500" color="white" borderRadius="full" borderWidth="1px" borderColor="accent.400" maxW="100%">
                <Text fontSize="xs" fontWeight={700} noOfLines={1}>📁 {localReference.name}</Text>
                <CloseButton size="sm" onClick={clearLocalReference} aria-label="Remove local reference" />
              </HStack>
            )}
            {!localReference && reference && (
              <ReferenceChip
                title={reference.title}
                artist={reference.artist}
                cover={reference.cover}
                onRemove={removeReference}
              />
            )}
            {prompt.trim() && (
              <Badge
                px={3} py={2} borderRadius="full" bg="accent.500" color="white"
                fontWeight={600} textTransform="none" fontSize="sm" maxW="100%"
              >
                <Text as="span" noOfLines={1}>💬 {prompt.trim()}</Text>
              </Badge>
            )}
            {instrumental && (
              <Badge px={3} py={2} borderRadius="full" colorScheme="purple" textTransform="none" fontSize="sm">🎹 instrumental</Badge>
            )}
            {soundPresets.length > 0 && (
              <PresetChips
                presets={soundPresets}
                onRemove={(id) => setSoundPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            )}
            {!instrumental && lyricsPresets.length > 0 && (
              <PresetChips
                presets={lyricsPresets}
                onRemove={(id) => setLyricsPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            )}
            {!reference && !localReference && !prompt.trim() && !instrumental && soundPresets.length === 0 && lyricsPresets.length === 0 && (
              <Text fontSize="sm" color="whiteAlpha.500">
                {t('generator.selectionEmpty', 'Nothing selected yet — add a prompt, tap a preset, or come back from a song page.')}
              </Text>
            )}
          </Flex>
        </Box>

        {/* --- Generate --- */}
        {error && (
          <Alert status="error" borderRadius="10px" mb={4} bg="red.900" color="whiteAlpha.900" borderWidth="1px" borderColor="red.500">
            <AlertIcon color="red.300" />
            {error}
          </Alert>
        )}
        <Button
          size="lg"
          w="full"
          h="56px"
          onClick={handleGenerate}
          isLoading={isGenerating}
          loadingText={t('generator.working', 'Generating…')}
          isDisabled={!canGenerate}
          data-cta="generator:submit"
          borderRadius="14px"
          bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
          color="white"
          fontWeight={700}
          fontSize="16px"
          letterSpacing="0.01em"
          boxShadow="0 12px 32px rgba(233,74,143,0.35)"
          _hover={{
            bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)',
            transform: 'translateY(-1px)',
            boxShadow: '0 16px 40px rgba(233,74,143,0.45)',
          }}
          _active={{ transform: 'translateY(0)' }}
          _disabled={{
            bgGradient: 'linear(90deg, #4A2A3F 0%, #3A2A55 100%)',
            color: 'whiteAlpha.500',
            boxShadow: 'none',
            cursor: 'not-allowed',
            _hover: {
              bgGradient: 'linear(90deg, #4A2A3F 0%, #3A2A55 100%)',
              transform: 'none',
            },
          }}
        >
          {currentJobId && isJobComplete
            ? t('generator.generateAnother', 'Generate another')
            : t('generator.generate', 'Generate song')}
        </Button>
        <HStack justify="center" mt={3} spacing={2} color="whiteAlpha.700" fontSize="sm">
          <Checkbox
            isChecked={withCover}
            onChange={(e) => setWithCover(e.target.checked)}
            colorScheme="pink"
            size="sm"
          >
            <HStack spacing={1.5} as="span">
              <PictoolsMark size={14} />
              <Text as="span" fontSize="sm">
                {t('generator.withCover', 'Also create cover art with pictools.ai (+1 credit)')}
              </Text>
            </HStack>
          </Checkbox>
        </HStack>
        <HStack justify="center" mt={2} color="whiteAlpha.600" fontSize="xs" fontFamily="ui-monospace, SF Mono, Menlo, monospace" letterSpacing="0.05em">
          <Text>⚡ {withCover ? t('generator.cost2', '2 CREDITS — SONG + COVER') : t('generator.cost1', '1 CREDIT')}</Text>
        </HStack>
        {!hasLyricsIntent && hasBaseSignal && (
          <Text mt={2} textAlign="center" fontSize="xs" color="yellow.300">
            {t('generator.needLyricsOrInstr', 'Write lyrics, draft some, or tick Instrumental to enable generation.')}
          </Text>
        )}

        {/* --- Job status --- */}
        {currentJobId && (
          <Box
            mt={7}
            bg="rgba(20, 14, 36, 0.65)"
            backdropFilter="blur(14px)"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="18px"
            p={{ base: 4, md: 5 }}
            boxShadow="0 8px 24px rgba(0,0,0,0.28)"
          >
            <HStack spacing={3} mb={currentJob?.status === 'completed' && (currentJob?.outputAudioUrl || currentJob?.outputUrl) ? 4 : 0}>
              {isJobComplete ? (
                <CheckCircleIcon color={currentJob?.status === 'completed' ? 'green.400' : 'red.400'} />
              ) : (
                <Spinner size="sm" color="accent.400" />
              )}
              <Stack spacing={0} flex="1">
                <Text color="whiteAlpha.900" fontWeight={600}>
                  {isJobComplete
                    ? currentJob?.status === 'completed'
                      ? t('generator.done', 'Done')
                      : t('generator.jobFailed', 'Generation failed')
                    : t('generator.working', 'Generating…')}
                </Text>
                <Text color="whiteAlpha.600" fontSize="xs">Job #{currentJobId.slice(-8)}</Text>
              </Stack>
              {isJobComplete && (
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    colorScheme="pink"
                    variant="solid"
                    onClick={handleGenerate}
                    isDisabled={!canGenerate}
                    data-cta="generator:submit-again"
                  >
                    ↻ {t('generator.generateAnother', 'Generate another')}
                  </Button>
                  <Button as={Link} to="/songs" size="sm" variant="outline" colorScheme="whiteAlpha">
                    {t('generator.viewAll', 'My songs')}
                  </Button>
                </HStack>
              )}
            </HStack>
            {currentJob?.status === 'completed' && (currentJob?.outputAudioUrl || currentJob?.outputUrl) && (
              <Box bg="whiteAlpha.50" borderRadius="8px" p={3} borderWidth="1px" borderColor="whiteAlpha.100">
                <Text fontSize="sm" color="whiteAlpha.700">
                  {t('generator.nowPlaying', '🎵 Playing in the bottom player — use it to pause, seek and control volume.')}
                </Text>
                <HStack mt={2} spacing={4} fontSize="xs">
                  <Text
                    as="button"
                    color="accent.400"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={() => downloadSongWithTags(
                      currentJob.outputAudioUrl || currentJob.outputUrl,
                      currentJob.title || t('generator.untitled', 'MixGenie song'),
                    )}
                    data-cta="generator:download"
                  >
                    ⬇ {t('generator.download', 'Download')}
                  </Text>
                </HStack>
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* --- Out-of-credits paywall --- */}
      <Modal isOpen={paywall.isOpen} onClose={paywall.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>{t('generator.paywallTitle', "You're out of credits")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3} color="whiteAlpha.800">
              {t('generator.paywallBody', 'Grab a credit pack or subscribe to keep generating.')}
            </Text>
            <List spacing={2} fontSize="sm">
              {[t('generator.pw1', 'Unlimited song generation'), t('generator.pw2', 'Priority queue'), t('generator.pw3', 'HD downloads')].map((b) => (
                <ListItem key={b}><ListIcon as={CheckCircleIcon} color="accent.400" />{b}</ListItem>
              ))}
            </List>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="pink" w="full" onClick={() => { paywall.onClose(); navigate('/credits'); }}>
              {t('generator.seePlans', 'See plans')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Full-screen "we're working on it" overlay. Open while the FE is
          submitting (`isGenerating`) OR the server-side job is still in a
          non-terminal state. Cancel dismisses the overlay — it does NOT
          actually kill the Mureka job (no cancel endpoint), it just
          returns the user to the form so they can adjust or wait. */}
      <GenerationOverlay
        isOpen={isGenerating || (!!currentJobId && !isJobComplete)}
        instrumental={instrumental}
        onCancel={() => {
          setIsGenerating(false);
          setCurrentJobId(null);
        }}
      />
    </>
  );
}

// -----------------------------------------------------------------------------
// Small building blocks kept in-file — the rest of the app has no other
// consumers, and inlining them keeps the page self-describing.
// -----------------------------------------------------------------------------

function FieldBlock({ label, hint, right, children }: { label: string; hint?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box
      mb={4}
      bg="rgba(20, 14, 36, 0.55)"
      backdropFilter="blur(14px)"
      border="1px solid"
      borderColor="whiteAlpha.150"
      borderRadius="18px"
      p={{ base: 4, md: 5 }}
      boxShadow="0 8px 24px rgba(0,0,0,0.25)"
    >
      <Flex align="center" justify="space-between" mb={2} gap={3} wrap="wrap">
        <Text
          fontSize="11px"
          fontWeight={700}
          color="whiteAlpha.700"
          textTransform="uppercase"
          letterSpacing="0.1em"
        >{label}</Text>
        {right}
      </Flex>
      {hint && <Text fontSize="xs" color="whiteAlpha.600" mb={3}>{hint}</Text>}
      {children}
    </Box>
  );
}

function ReferenceChip({ title, artist, cover, onRemove }: { title: string; artist: string; cover?: string; onRemove: () => void }) {
  return (
    <HStack
      spacing={2} pl={cover ? 1 : 3} pr={1} py={1}
      bg="pink.900" color="whiteAlpha.900" borderRadius="full"
      borderWidth="1px" borderColor="pink.500" maxW="100%"
    >
      {cover && (
        <Image
          src={catalogCoverImage(cover, 'song')}
          alt={`${title} by ${artist}`}
          boxSize="28px" borderRadius="full" objectFit="cover"
        />
      )}
      <Stack spacing={0} minW={0}>
        <Text fontSize="xs" fontWeight={700} noOfLines={1}>🎵 {title}</Text>
        <Text fontSize="xs" color="whiteAlpha.700" noOfLines={1}>{artist}</Text>
      </Stack>
      <CloseButton size="sm" onClick={onRemove} aria-label="Remove reference" />
    </HStack>
  );
}
